import React, { ReactNode, useContext, useEffect, useLayoutEffect, useRef, useState } from "react";
import { Container, Group, Text, Badge, Accordion, ScrollArea, Stack, ColorSwatch, Loader, Center } from "@mantine/core";
import { PanelGroup, Panel } from "react-resizable-panels";
import DiffExprDetails from "../components/EvidenceDetails/DiffExprDetails";
import TxtMiningDetails from "../components/EvidenceDetails/TxtMiningDetails";
import OtpDetails from "../components/EvidenceDetails/OtpDetails";
import WorkflowDiagram from "../components/EvidenceDetails/Workflow/draw"
import ResizablePanelHandle from "../components/Layout";
import UiStateContext from "../providers/UiStateProvider";
import DataContext from "../providers/DataProvider";
import { getEvidence, getEvidenceSummary } from "../services/evidence";
import UserSelectionContext from "../providers/UserSelectionProvider";
import { dataStreamTypes, scoreTypes } from "../types";
import { getHardcodedColor, getHardcodedLabels } from "../utils";
import { FiActivity } from "react-icons/fi";
import { FaDna, FaFlask } from "react-icons/fa";
import { TbFileTime, TbFile, TbFunction } from "react-icons/tb";

import PlotFunction from "../components/EvidenceDetails/SaturationDetails";

enum DStreamTypes {
  txtMiningOverall = "txt_mining_overall",
  txtMiningNovelty = "txt_mining_novelty",
  DiffExpr = "deff_expr",
  OTP = "otp",
}

interface Filter {
  stream: DStreamTypes | undefined
  eid: number | undefined
  gene: string | undefined
  inline: boolean | undefined
}

export const LeftPanel = ({ sankeyGraph, w, alpha, onFilterSelected, onSaturationSelected }) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const [dimensions, setDimensions] = useState({ width: 1000, height: 800 })
  // const { sankeyGraph, selectedRun, weights } = useContext(DataContext)
  // const alpha = weights[selectedRun.run_id].alpha
  // const w = weights[selectedRun.run_id].weights

  useEffect(() => {
    setDimensions((prev) => ({
      ...prev,
      height: containerRef.current!.clientHeight,
    }));
  }, []);

  useLayoutEffect(() => {
    const observer = new ResizeObserver((entries) => {
      for (let entry of entries) {
        setDimensions((prev) => ({
          ...prev,
          width: entry.contentRect.width,
        }));
      }
    });

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => {
      if (containerRef.current) observer.unobserve(containerRef.current);
    };
  }, []);

  return (
    <Stack
      gap={3}
      ref={containerRef}
      style={{
        width: "100%",
        padding: "1%",
        overflow: 'auto'
      }}
    >
      <Group justify="flex-start" style={{ padding: "1%" }}>
        {Object.values(dataStreamTypes).map((t) =>
          <Group>
            <ColorSwatch
              component="button"
              size={20}
              color={getHardcodedColor(t)}
              style={{ color: '#fff' }}
            >
            </ColorSwatch>
            <Text size="sm">{getHardcodedLabels(t)}</Text>
          </Group>
        )}
        <Group>
          <svg width="20" height="20">
            <circle
              cx="10" cy="10" r="8"
              style={{ fill: 'url(#diagonalHatch)', stroke: 'black', strokeWidth: '1' }}
            />
          </svg>
          <Text size="sm">Negative Contribution</Text>
        </Group>
      </Group>
      {sankeyGraph && alpha && w && dimensions.height && dimensions.width &&
        <WorkflowDiagram
          data={sankeyGraph}
          alpha={alpha} w={w}
          onLinkSelected={onFilterSelected}
          onSaturationSelected={onSaturationSelected}
          width={dimensions.width * 0.9}
          height={dimensions.height * 0.8}
        />}
    </Stack>
  );
};

interface AccordionLabelProps {
  label: string;
  icon: ReactNode;
  color: string;
  description?: string;
  descriptionColor?: string;
  loading?: boolean;
}

function AccordionLabel({ label, icon, color, description = '', descriptionColor = 'dimmed', loading = false }: AccordionLabelProps) {
  return (
    <Group wrap="nowrap">
      {icon}
      <Group>
        <Badge size="lg" color={color}>{label}</Badge>
        <Text size="xs" c={descriptionColor} fw={400}>
          {description}
        </Text>
        {loading && <Loader size="sm" />}
      </Group>
    </Group>
  );
}

export const RightPanel: React.FC<{ filters: Filter[], details: any[], selectedSaturation: any }> = ({ filters, details, selectedSaturation }) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 })

  useEffect(() => {
    setDimensions((prev) => ({
      ...prev,
      height: containerRef.current!.clientHeight,
    }));
  }, []);

  useLayoutEffect(() => {
    const observer = new ResizeObserver((entries) => {
      for (let entry of entries) {
        setDimensions((prev) => ({
          ...prev,
          width: entry.contentRect.width,
        }));
      }
    });

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => {
      if (containerRef.current) observer.unobserve(containerRef.current);
    };
  }, []);

  const has_stream = (stream) => filters.find(f => f.stream === stream)

  return (
    <div
      ref={containerRef}
      style={{
        height: "90%",
        marginBottom: "200px"
      }}
    >
      {<Accordion value={!filters.length && selectedSaturation ? 'saturation' : !selectedSaturation && filters.every(f => f.stream === filters[0].stream) ? filters[0].stream : undefined}>
        {
          selectedSaturation &&
          <Accordion.Item key="saturation" value="saturation">
            <Accordion.Control><AccordionLabel icon={<TbFunction size={25} />}
              label="Saturation Function" color={getHardcodedColor(selectedSaturation.base)}
              description={getHardcodedLabels(selectedSaturation.base)} /></Accordion.Control>
            <Accordion.Panel
              style={{
                margin: 0,
                padding: 0
              }}>
              <PlotFunction
                key={`saturation-${selectedSaturation}`}
                data={{ alpha: selectedSaturation.alpha, input: selectedSaturation.input, xMax: selectedSaturation.xMax }}
                width={dimensions.width}
                height={0.7 * dimensions.width}
              />
            </Accordion.Panel>
          </Accordion.Item>
        }
        {
          (has_stream(dataStreamTypes.diffexpr)) &&
          <Accordion.Item key={dataStreamTypes.diffexpr} value={dataStreamTypes.diffexpr}>
            <Accordion.Control><AccordionLabel icon={<FiActivity size={25} />} label={getHardcodedLabels(dataStreamTypes.diffexpr)} color={getHardcodedColor(dataStreamTypes.diffexpr)} loading={!details[dataStreamTypes.diffexpr]} /></Accordion.Control>
            <Accordion.Panel
              style={{
                margin: 0,
                padding: 0
              }}>
              {details[dataStreamTypes.diffexpr] && <DiffExprDetails
                key={`diffexpr-${filters}`}
                data={details[dataStreamTypes.diffexpr][0]}
                filters={filters}
                width={dimensions.width}
                height={0.7 * dimensions.width} />}
            </Accordion.Panel>
          </Accordion.Item>
        }
        {
          (has_stream(dataStreamTypes.otg)) &&
          <Accordion.Item key={dataStreamTypes.otg} value={dataStreamTypes.otg}>
            <Accordion.Control><AccordionLabel icon={<FaFlask size={25} />} label={getHardcodedLabels(dataStreamTypes.otg)} color={getHardcodedColor(dataStreamTypes.otg)} loading={!details[dataStreamTypes.otg]} /></Accordion.Control>
            <Accordion.Panel
              style={{
                margin: 0,
                padding: 0
              }}>
              {details[dataStreamTypes.otg] && <DiffExprDetails
                key={`bbprot-${filters}`}
                data={details[dataStreamTypes.otg][0]}
                filters={filters}
                width={dimensions.width}
                height={0.7 * dimensions.width} />}
            </Accordion.Panel>
          </Accordion.Item>
        }
        {
          (has_stream(dataStreamTypes.miningNovelty)) &&
          <Accordion.Item key={dataStreamTypes.miningNovelty} value={dataStreamTypes.miningNovelty}>
            <Accordion.Control><AccordionLabel icon={<TbFileTime size={25} />} label={getHardcodedLabels(dataStreamTypes.miningNovelty)} color={getHardcodedColor(dataStreamTypes.miningNovelty)} loading={!details[dataStreamTypes.miningNovelty]} /></Accordion.Control>
            <ScrollArea>
              <Accordion.Panel
                style={{
                  display: 'inline-block',
                  whiteSpace: 'nowrap',
                  overflow: 'visible',
                  minHeight: 250
                }}>
                {details[dataStreamTypes.miningNovelty] && <TxtMiningDetails
                  key={`txtmining-novelty-${filters}`}
                  type={dataStreamTypes.miningNovelty}
                  data={details[dataStreamTypes.miningNovelty]}
                  filters={filters}
                  width={dimensions.width}
                  height={0.5 * dimensions.width} />}
              </Accordion.Panel>
            </ScrollArea>
          </Accordion.Item>
        }
        {
          (has_stream(dataStreamTypes.miningOverall)) &&
          <Accordion.Item key={dataStreamTypes.miningOverall} value={dataStreamTypes.miningOverall}>
            <Accordion.Control><AccordionLabel icon={<TbFile size={25} />} label={getHardcodedLabels(dataStreamTypes.miningOverall)} color={getHardcodedColor(dataStreamTypes.miningOverall)} loading={!details[dataStreamTypes.miningOverall]} /></Accordion.Control>
            <ScrollArea>
              <Accordion.Panel
                style={{
                  display: 'inline-block',
                  whiteSpace: 'nowrap',
                  overflow: 'visible',
                  minHeight: 250
                }}>
                {details[dataStreamTypes.miningOverall] && <TxtMiningDetails
                  key={`txtmining-overall-${filters}`}
                  type={dataStreamTypes.miningOverall}
                  data={details[dataStreamTypes.miningOverall]}
                  filters={filters}
                  width={dimensions.width}
                  height={0.5 * dimensions.width} />}
              </Accordion.Panel>
            </ScrollArea>
          </Accordion.Item>
        }
        {
          (has_stream(dataStreamTypes.otp)) &&
          <Accordion.Item key={dataStreamTypes.otp} value={dataStreamTypes.otp}>
            <Accordion.Control><AccordionLabel icon={<FaDna size={25} />} label={getHardcodedLabels(dataStreamTypes.otp)} color={getHardcodedColor(dataStreamTypes.otp)} loading={!details[dataStreamTypes.otp]} /></Accordion.Control>
            <ScrollArea>
              <Accordion.Panel
                style={{
                  display: 'inline-block',
                  whiteSpace: 'nowrap',
                  overflow: 'visible',
                  minHeight: 250,
                  maxWidth: dimensions.width
                }}>
                {details[dataStreamTypes.otp] && <OtpDetails
                  key={`otp-${filters}`}
                  data={details[dataStreamTypes.otp][0]}
                  filters={filters}
                  width={dimensions.width}
                  height={500} />}
              </Accordion.Panel>
            </ScrollArea>
          </Accordion.Item>
        }
      </Accordion>}
    </div>
  );
};

const Evidence: React.FC = () => {
  const { selectedRun, weights } = useContext(DataContext)
  const { selectedMoa, focusedIndications, selectedWeights } = useContext(UserSelectionContext)
  const [sankeyGraph, setSankeyGraph] = useState<any>(null);
  const [selectedFilter, setSelectedFilter] = useState([])
  const [selectedSaturation, setSelectedSaturation] = useState<{ id: number, input: number, alpha: number, xMax: number, base: string }>(null)
  const [details, setDetails] = useState({})
  const { SwitchMeshPanelOpened, meshPanelOpened } = useContext(UiStateContext)

  useEffect(() => {
    if (!weights || !selectedWeights || !selectedRun || !selectedMoa || !focusedIndications) return
    setSankeyGraph(null)
    const callGetSankey = async () => {
      const sankeyData = await getEvidenceSummary(selectedRun.run_id, selectedMoa, focusedIndications)
      setSankeyGraph(sankeyData)
    }
    callGetSankey()
  }, [focusedIndications, selectedRun, weights, selectedMoa]);

  useEffect(() => {
    if (!weights || !selectedWeights || !selectedRun || !selectedMoa || !focusedIndications) return;
    setDetails({});

    const streamsToFetch = Object.values(dataStreamTypes).filter(
      (stream) => stream !== dataStreamTypes.miningNovelty
    );

    let isCancelled = false;

    const fetchAllStreamsSequentially = async () => {
      for (const stream of streamsToFetch) {
        const detailsResp = await getEvidence(stream, selectedRun.run_id, selectedMoa, focusedIndications);

        if (isCancelled) return;

        let linkedDetails = {};
        if (stream === dataStreamTypes.miningOverall) {
          linkedDetails[dataStreamTypes.miningNovelty] = detailsResp;
        }

        setDetails((prev) => ({
          ...prev,
          [stream]: detailsResp,
          ...linkedDetails
        }));
      }
    };

    fetchAllStreamsSequentially();

    return () => { 
      isCancelled = true; 
    };

  }, [sankeyGraph]);


  const onFilterSelected = async (links) => {
    if (!focusedIndications) return;
    if (links.length === 0) {
      setSelectedFilter([]);
      return;
    }
    if (meshPanelOpened) {
      SwitchMeshPanelOpened();
    }

    setSelectedFilter(links.map(l => l.filters));
  }

  const onSaturationSelected = (node: { id: number, input: number, alpha: number, xMax: number, base: string }) => {
    setSelectedSaturation(node)
  }

  return (
    <Container fluid style={{ display: "flex", height: "100vh", padding: 0 }}>
      <PanelGroup direction="horizontal">
        {weights && weights[selectedRun.run_id] && weights[selectedRun.run_id]['alpha'] && weights[selectedRun.run_id]['A'] && selectedRun && <Panel minSize={50} order={1} >
          {sankeyGraph && sankeyGraph.length ? <LeftPanel onSaturationSelected={onSaturationSelected} onFilterSelected={onFilterSelected} alpha={weights[selectedRun.run_id].alpha} sankeyGraph={sankeyGraph} w={weights[selectedRun.run_id]['A']} /> :
            <Center style={{ height: "100%", width: "100%" }}>
              <Loader size="xl" />
            </Center>}
        </Panel>}
        {sankeyGraph && sankeyGraph.length && (selectedFilter.length > 0 || selectedSaturation) && (
          <>
            <ResizablePanelHandle />
            <Panel minSize={25} defaultSize={25} order={2}>
              <RightPanel filters={selectedFilter} details={details} selectedSaturation={selectedSaturation} />
            </Panel>
          </>
        )}
      </PanelGroup>
    </Container>
  );
};

export default Evidence;
